import React, { Suspense } from "react";
import Loader from "components/loader";
import { Routes, Route, Navigate } from "react-router-dom";
import Error404 from "pages/error-404";
import { useOrderedNavItems } from "components/left-navigation/hooks";
import { useSelectedTeam } from "features/team-settings/hooks";
import { useHeaderTeamNameSelector } from "pages/team_settings/hooks";
import { SideNavigationProps } from "@amzn/awsui-components-react/polaris/side-navigation";
/* PLOP_IMPORT_ROUTES */
import Home from "pages/home";
import ApproverSelectionPage, { KBApprovalDetailPage } from "pages/kb-approval";
import AgentActionReviewDetailPage from "pages/agent-action-review";
import TeamFiles from "pages/team-files";
import Drafts from "pages/drafts";
import Dashboard from "pages/dashboard";
import GaiaAi from "pages/gaia-ai";
import Chatbot from "pages/chatbot";
import CreateEditGaiaObject from "pages/create-edit-gaiaobject";
import GaiaObjects from "pages/gaia-objects";
import ContactInformation from "pages/contact-information";
import EditContact from "pages/edit-contact";
import CreateContact from "pages/create-contact";
import CreateEditAssignmentGroup from "pages/create-edit-assignment-group";
import OutcomeOverview from "pages/outcome-overview";
import EditPayment from "pages/edit-payment";
import EditAssessment from "pages/edit-assessment";
import CreateAssessment from "pages/create-assessment";
import CreatePayment from "pages/create-payment";
import Reviews from "pages/reviews";
import DataReportSelection from "pages/data-report-selection";
import Tasks from "pages/tasks";
import AuditOverview from "pages/audit-overview";
import CreateAudit from "pages/create-audit";
import EditAudit from "pages/edit-audit";
import Audits from "pages/audits";
import Data from "pages/data";
import DataInsightsList from "pages/data-insights";
import DataTypeSelection from "pages/data-insights/DataTypeSelection";
import DatasetCreateWizard from "pages/data-insights/DatasetCreateWizard";
import DatasetDetail from "pages/data-insights/DatasetDetail.lazy";
import DataQuery from "pages/data-query";
import DataCreate from "pages/data-create";
import TaskInformation from "pages/task-information";
import CreateEditTask from "pages/create-edit-task";
import TeamSettings from "pages/team_settings";
import SiteAdmin from "pages/site_admin";
import SchemaEditor from "pages/site_admin/schema_editor";
import GaiaObjectOverview from "pages/gaia-object-overview";
import ConversationalHistory from "pages/conversational-history";
import DraftOverview from "pages/draft-overview";
import AgentWork from "pages/agent-work";
import Events from "pages/events";
import CreateEvent from "pages/create-event";
import EventInformation from "pages/event-information";

const Router: React.FC = (): JSX.Element => {
  const { items, isLoading } = useOrderedNavItems();
  const { isHomePageEnabled } = useSelectedTeam();
  const teamName = useHeaderTeamNameSelector();
  const isGuest = teamName === "guest";

  const primaryFeature = (
    items.find((item) => item.type === "link") as SideNavigationProps.Link
  )?.href;

  const navigateTo = (page: JSX.Element) => {
    if (page.type && page.type.name === "TeamSettings") {
      return <Navigate replace to="/team" />;
    }
    return page;
  };

  return (
    <Suspense fallback={<Loader loadingText="Loading..." />}>
      {/* <Provider store={store}> */}
      <Routes>
        <Route
          path="/"
          element={
            isHomePageEnabled || isGuest ? (
              <Navigate replace to="/home" />
            ) : !isLoading && primaryFeature ? (
              <Navigate replace to={primaryFeature} />
            ) : (
              <Loader loadingText="Loading..." />
            )
          }
        />

        {/* PLOP_INJECT_ROUTES */}
        <Route path="/home" element={<Home />} />
        <Route
          path="/reviews/kb-approvals/:reviewId"
          element={<KBApprovalDetailPage />}
        />
        <Route
          path="/agent-action-reviews/:reviewId"
          element={<AgentActionReviewDetailPage />}
        />
        <Route path="/kb/approval/new" element={<ApproverSelectionPage />} />
        <Route
          path="/kb/deletion-approval/new"
          element={<ApproverSelectionPage />}
        />
        <Route
          path="/kb/existing-doc-approval/new"
          element={<ApproverSelectionPage />}
        />
        <Route path="/team-files/*" element={<TeamFiles />} />
        <Route
          path="/litigations/new"
          element={<CreateEditGaiaObject type="litigation" mode="create" />}
        />
        <Route
          path="/notices/new"
          element={<CreateEditGaiaObject type="notice" mode="create" />}
        />
        <Route path="/events/new" element={<CreateEvent />} />
        <Route
          path="/litigations/:shortId/edit"
          element={<CreateEditGaiaObject type="litigation" mode="edit" />}
        />
        <Route
          path="/litigations/:shortId/edit-payment/:paymentId"
          element={navigateTo(<EditPayment />)}
        />
        <Route
          path="/litigations/:shortId/edit-assessment/:assessmentId"
          element={navigateTo(<EditAssessment />)}
        />
        <Route
          path="/litigations/:shortId/create-assessment"
          element={navigateTo(<CreateAssessment />)}
        />
        <Route
          path="/litigations/:shortId/create-payment"
          element={navigateTo(<CreatePayment />)}
        />
        <Route
          path="/litigations/:shortId/assessment/:assessmentId"
          element={navigateTo(<OutcomeOverview />)}
        />
        <Route
          path="/litigations/:shortId/payment/:paymentId"
          element={navigateTo(<OutcomeOverview />)}
        />
        <Route
          path="/notices/:shortId/edit"
          element={<CreateEditGaiaObject type="notice" mode="edit" />}
        />
        <Route
          path="/notices/:shortId/edit-payment/:paymentId"
          element={navigateTo(<EditPayment />)}
        />
        <Route
          path="/notices/:shortId/edit-assessment/:assessmentId"
          element={navigateTo(<EditAssessment />)}
        />
        <Route
          path="/notices/:shortId/create-assessment"
          element={navigateTo(<CreateAssessment />)}
        />
        <Route
          path="/notices/:shortId/create-payment"
          element={navigateTo(<CreatePayment />)}
        />
        <Route
          path="/notices/:shortId/assessment/:assessmentId"
          element={navigateTo(<OutcomeOverview />)}
        />
        <Route
          path="/notices/:shortId/payment/:paymentId"
          element={navigateTo(<OutcomeOverview />)}
        />
        <Route path="/events/:shortId" element={<EventInformation />} />
        <Route path="/events/:shortId/edit" element={<EventInformation />} />
        <Route path="/gaia-ai" element={<GaiaAi />} />
        <Route path="/drafts" element={<Drafts />} />
        <Route path="/gaia-ai/chatbot" element={<Chatbot key="base" />} />
        <Route
          path="/gaia-ai/chatbot/:conversationId"
          element={<Chatbot key="conversation" />}
        />
        <Route
          path="/gaia-ai/conversation-history"
          element={<ConversationalHistory />}
        />
        <Route
          path="/litigations"
          element={<GaiaObjects key="litigation" type="litigation" />}
        />
        <Route
          path="/notices"
          element={<GaiaObjects key="notice" type="notice" />}
        />
        <Route path="/events" element={<Events />} />
        <Route
          path="/team/contact-information/:contactId"
          element={<ContactInformation />}
        />
        <Route path="/team/edit-contact/:contactId" element={<EditContact />} />
        <Route path="/team/create-contact" element={<CreateContact />} />
        <Route
          path="/team/create-assignment-group"
          element={<CreateEditAssignmentGroup mode="create" />}
        />
        <Route
          path="/team/edit-assignment-group/:assignmentGroupId"
          element={<CreateEditAssignmentGroup mode="edit" />}
        />
        <Route
          path="/audit/:auditId/edit-payment/:paymentId"
          element={navigateTo(<EditPayment />)}
        />
        <Route
          path="/audit/:auditId/edit-assessment/:assessmentId"
          element={navigateTo(<EditAssessment />)}
        />
        <Route
          path="/audit/:auditId/create-assessment"
          element={navigateTo(<CreateAssessment />)}
        />
        <Route
          path="/audit/:auditId/create-payment"
          element={navigateTo(<CreatePayment />)}
        />
        <Route
          path="/audit/:auditId/assessment/:assessmentId"
          element={navigateTo(<OutcomeOverview />)}
        />
        <Route
          path="/audit/:auditId/payment/:paymentId"
          element={navigateTo(<OutcomeOverview />)}
        />
        <Route path="/team" element={<TeamSettings />} />
        <Route path="/site-admin" element={<SiteAdmin />} />
        <Route
          path="/site-admin/teams/:teamId/schema"
          element={<SchemaEditor />}
        />
        <Route path="/tasks" element={navigateTo(<Tasks />)} />
        <Route
          path="/tasks/create"
          element={navigateTo(<CreateEditTask mode="create" />)}
        />
        <Route
          path="/task/:taskId/edit"
          element={navigateTo(<CreateEditTask mode="edit" />)}
        />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/task/:taskId" element={navigateTo(<TaskInformation />)} />
        <Route path="/audit/:auditId" element={navigateTo(<AuditOverview />)} />
        <Route
          path="/audit/:auditId/edit"
          element={navigateTo(<EditAudit />)}
        />
        <Route
          path="/litigations/:shortId"
          element={navigateTo(<GaiaObjectOverview category="litigation" />)}
        />
        <Route
          path="/notices/:shortId"
          element={navigateTo(<GaiaObjectOverview category="notice" />)}
        />
        <Route
          path="/audit/create-audit"
          element={navigateTo(<CreateAudit />)}
        />
        <Route
          path="/audit/:auditId/data/:queryId"
          element={navigateTo(<DataQuery />)}
        />
        <Route
          path="/notices/:shortId/data/:queryId"
          element={navigateTo(<DataQuery />)}
        />
        <Route
          path="/litigations/:shortId/data/:queryId"
          element={navigateTo(<DataQuery />)}
        />
        <Route path="/audits/" element={navigateTo(<Audits />)} />
        <Route path="/agent-work" element={<AgentWork />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/data-insights/" element={<DataInsightsList />} />
        <Route path="/data-insights/new" element={<DataTypeSelection />} />
        <Route
          path="/data-insights/tar-extraction"
          element={<DatasetCreateWizard />}
        />
        <Route path="/datasets/:datasetId" element={<DatasetDetail />} />
        <Route path="/datasets/:datasetId/:tab" element={<DatasetDetail />} />
        <Route
          path="/datasets/:datasetId/export/:exportId"
          element={<DatasetDetail />}
        />
        <Route
          path="/datasets/:datasetId/report/:reportId"
          element={<DatasetDetail />}
        />
        <Route path="/data/" element={<Data />} />
        <Route path="/data/create" element={<DataCreate />} />
        <Route path="/data/:queryId" element={<DataQuery />} />
        <Route
          path="/data/data-report-selection"
          element={<DataReportSelection />}
        />
        <Route path="/drafts/:shortId" element={<DraftOverview />} />
        {/* Must be the last route */}
        <Route path="*" element={<Error404 />} />
      </Routes>
      {/* </Provider> */}
    </Suspense>
  );
};

export default Router;
